import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader, Card, Spinner, EmptyState } from "@/components/bits";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Wrench, Loader2 } from "lucide-react";
import PartPhotos from "@/components/PartPhotos";

export default function PartTerpasang() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inst, setInst] = useState(null);
  const [saving, setSaving] = useState(false);
  const [photoCount, setPhotoCount] = useState(0);

  const load = () => { setLoading(true); api.get("/parts/ready-install").then((r) => setRows(r.data)).finally(() => setLoading(false)); };
  useEffect(load, []);

  const openInstall = (r) => { setPhotoCount(0); setInst({ ...r, qty: r.available, technician: "", notes: "" }); };

  const submit = async () => {
    const qty = Number(inst.qty);
    if (qty <= 0 || qty > inst.available) return toast.error("Qty tidak valid");
    setSaving(true);
    try {
      await api.post("/parts/install", { job_id: inst.job_id, part_number: inst.part_number, part_name: inst.part_name, qty, technician: inst.technician, notes: inst.notes });
      toast.success("Ditandai INSTALLED");
      setInst(null); load();
    } catch (err) { toast.error(apiError(err, "Gagal")); } finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Part Terpasang" subtitle="Part yang sudah datang (available) dan siap dipasang. Mark Installed langsung." />
      {loading ? <Spinner /> : rows.length === 0 ? <EmptyState title="Tidak ada part siap dipasang" icon={Wrench} /> : (
        <div className="grid gap-2">
          {rows.map((r, i) => (
            <Card key={i} className="p-3 flex items-center justify-between gap-2" data-testid={`ready-${i}`}>
              <div>
                <div className="font-mono-data font-semibold">{r.nopol} • {r.part_number}</div>
                <div className="text-sm text-gray-600">{r.part_name}</div>
                <div className="text-xs text-gray-400">Available {r.available} • Installed {r.installed_qty}</div>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status="READY_INSTALL" />
                <Button size="sm" className="bg-gold text-white" onClick={() => openInstall(r)} data-testid={`mark-installed-${i}`}>
                  <Wrench size={14} className="mr-1" />Mark Installed
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!inst} onOpenChange={(o) => !o && setInst(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-head">Mark as Installed</DialogTitle></DialogHeader>
          {inst && (
            <div className="space-y-3">
              <div className="text-sm"><b className="font-mono-data">{inst.part_number}</b> — {inst.part_name} (avail {inst.available})</div>
              <div><label className="text-xs uppercase text-gray-500">Qty Installed</label><Input type="number" min={1} max={inst.available} value={inst.qty} onChange={(e) => setInst({ ...inst, qty: e.target.value })} className="mt-1" data-testid="install-qty" /></div>
              <div><label className="text-xs uppercase text-gray-500">Technician</label><Input value={inst.technician} onChange={(e) => setInst({ ...inst, technician: e.target.value })} className="mt-1" data-testid="install-tech" /></div>
              <div><label className="text-xs uppercase text-gray-500">Notes</label><Input value={inst.notes} onChange={(e) => setInst({ ...inst, notes: e.target.value })} className="mt-1" /></div>
              <div>
                <label className={`text-xs uppercase font-semibold ${photoCount >= 1 ? "text-emerald-600" : "text-red-500"}`}>
                  {photoCount >= 1 ? "✓ Foto Part Terpasang OK" : "Foto Part Terpasang (WAJIB, kamera) *"}
                </label>
                <div className="mt-1">
                  <PartPhotos partLineId={inst.part_line_id} phase="INSTALLED" canUpload compact maxPhotos={5}
                    meta={{ nopol: inst.nopol, claim_number: inst.claim_number, part_name: inst.part_name, part_number: inst.part_number }}
                    onCount={setPhotoCount} />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setInst(null)}>Batal</Button>
            <Button onClick={submit} disabled={saving || photoCount < 1} className="bg-gray-900" data-testid="confirm-install-btn">{saving ? <Loader2 className="animate-spin mr-2" size={16} /> : null}Confirm Installed</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
